import { useSelector, shallowEqual } from 'react-redux';
import { useOnboardingStatus } from './useOnboardingStatus';
import { USER_ROLES } from '../constants/userRoles';
import ROUTES from '../constants/routes';

/** Onboarding wizard, create-org flow, and cleaning standards step are always part of the flow (no feature flags). */
const ORGANIZATION_CREATE_FLOW_ENABLED = true;
const ONBOARDING_WIZARD_ENABLED = true;
const CLEANING_STANDARDS_ENABLED = true;

/**
 * useAuthRedirect
 *
 * - getCurrentUser returns organizationId null → only then we go through CreateOrganization (step 1).
 * - getCurrentUser returns valid organizationId → we use GET /api/v3/onboarding/status for redirect (no create-org).
 */
const useAuthRedirect = () => {

  const { isAuthenticated, userRole, orgId, needsInitialSetup, fullName, phoneNumber, onboardingDataFromLogin } =
    useSelector(
      (state) => ({
        isAuthenticated: state.auth.isAuthenticated,
        userRole: state.auth.user?.role,
        orgId: state.auth.user?.organization ?? state.auth.user?.organizationId,
        needsInitialSetup: state.auth.user?.needsInitialSetup,
        fullName: state.auth.user?.fullName,
        phoneNumber: state.auth.user?.phoneNumber,
        /** Set in login thunk after getCurrentUser + org info; used so status runs in correct order. */
        onboardingDataFromLogin: state.auth.onboardingData,
      }),
      shallowEqual,
    );

  const {
    data: onboardingDataFromHook,
    loading: onboardingLoading,
    error: onboardingError,
  } = useOnboardingStatus(orgId, { skipFetch: !!onboardingDataFromLogin });

  /** Prefer onboarding data from login (getCurrentUser → org → status); else from hook (e.g. refresh). */
  const onboardingData = onboardingDataFromLogin ?? onboardingDataFromHook;

  if (!isAuthenticated) {
    return { redirectTo: null, isLoadingRedirect: false, redirectError: null };
  }

  const profileIncomplete =
    fullName == null ||
    fullName === '' ||
    phoneNumber == null ||
    phoneNumber === '';

  const hasNoOrg = orgId == null || orgId === '';

  // Validate first: create-organization only when getCurrentUser returned organizationId null (no org yet).
  // When hasNoOrg we never call or use onboarding/status for redirect.
  if (ORGANIZATION_CREATE_FLOW_ENABLED && hasNoOrg) {
    return {
      redirectTo: `${ROUTES.CREATE_ORG}?step=1`,
      isLoadingRedirect: false,
      redirectError: null,
    };
  }

  // When orgId is valid we use /api/v3/onboarding/status (below); never redirect to create-org.

  // If onboarding status has step1 or step3 completed === false → organization/configuration (not complete_profile)
  if (orgId && onboardingData?.steps) {
    const steps = onboardingData.steps || {};
    const step1 = steps.step1_organization_structure ?? steps.facility_locations;
    const step3 = steps.step3_cleaning_standards;
    const step1Done = typeof step1 === 'boolean' ? step1 : step1?.completed === true;
    const step3Done = typeof step3 === 'boolean' ? step3 : step3?.completed === true;
    const onboardingIncomplete =
      !step1Done || (CLEANING_STANDARDS_ENABLED && !step3Done);
    if (onboardingIncomplete) {
      return {
        redirectTo: ROUTES.ORGANIZATION_CONFIGURATION,
        isLoadingRedirect: false,
        redirectError: null,
      };
    }
  }

  // Profile incomplete (fullName or phoneNumber missing) → complete profile
  if (profileIncomplete) {
    return {
      redirectTo: ROUTES.COMPLETE_PROFILE,
      isLoadingRedirect: false,
      redirectError: null,
    };
  }

  if (needsInitialSetup === true) {
    if (!ORGANIZATION_CREATE_FLOW_ENABLED) {
      return {
        redirectTo: ROUTES.FACILITY_SELECTION,
        isLoadingRedirect: false,
        redirectError: null,
      };
    }
    // needsInitialSetup but orgId valid → use onboarding status (fall through to role/onboarding logic), not create-org
  }

  if (!orgId) {
    return {
      redirectTo: ROUTES.FACILITY_SELECTION,
      isLoadingRedirect: false,
      redirectError: null,
    };
  }

  // Case D) Role is neither PRIMARY_ADMIN nor ORGANIZATION_ADMIN → go to FACILITY_SELECTION
  // These users go directly to the dashboard (facility selection is the entry point)
  if (userRole !== USER_ROLES.PRIMARY_ADMIN && userRole !== USER_ROLES.ORGANIZATION_ADMIN) {
    return {
      redirectTo: ROUTES.FACILITY_SELECTION,
      isLoadingRedirect: false,
      redirectError: null,
    };
  }

  // Case E) Role is ORGANIZATION_ADMIN → go to FACILITY_SELECTION
  // Organization admins go directly to the dashboard
  if (userRole === USER_ROLES.ORGANIZATION_ADMIN) {
    return {
      redirectTo: ROUTES.FACILITY_SELECTION,
      isLoadingRedirect: false,
      redirectError: null,
    };
  }

  // We only get here if userRole === PRIMARY_ADMIN and orgId exists and needsInitialSetup === false

  // Onboarding wizard is always enabled (no feature flag)
  if (!ONBOARDING_WIZARD_ENABLED) {
    return {
      redirectTo: ROUTES.FACILITY_SELECTION,
      isLoadingRedirect: false,
      redirectError: null,
    };
  }

  // Case F) PRIMARY_ADMIN with orgId (but onboarding is pending)
  // If still loading or data is not available yet → wait
  if (onboardingLoading || onboardingData == null) {
    return {
      redirectTo: null,
      isLoadingRedirect: true,
      redirectError: onboardingError,
    };
  }

  // Case G) PRIMARY_ADMIN with orgId and failed to load onboarding → go to ERROR_PAGE
  if (onboardingError) {
    return {
      redirectTo: ROUTES.ERROR_PAGE || null,
      isLoadingRedirect: false,
      redirectError: onboardingError,
    };
  }

  // Case H) PRIMARY_ADMIN with orgId and onboarding data (GET /api/v3/onboarding/status)
  // API returns: { success, data: { isComplete, canAccessDashboard, steps: { step1_organization_structure: { completed }, step3_cleaning_standards: { completed } } } }
  const steps = onboardingData.steps || {};
  const step1 =
    steps.step1_organization_structure ?? steps.facility_locations;
  const step1Done =
    typeof step1 === 'boolean' ? step1 : step1?.completed === true;
  const step3 = steps.step3_cleaning_standards;
  const step3Done =
    typeof step3 === 'boolean' ? step3 : step3?.completed === true;
  const onboardingStepsComplete = CLEANING_STANDARDS_ENABLED
    ? step1Done && step3Done
    : step1Done;

  if (onboardingStepsComplete) {
    return {
      redirectTo: ROUTES.DASHBOARD,
      isLoadingRedirect: false,
      redirectError: null,
    };
  }
  return {
    redirectTo: ROUTES.ORGANIZATION_CONFIGURATION,
    isLoadingRedirect: false,
    redirectError: null,
  };

  // We will NEVER reach this point because all previous branches return something.
};

export default useAuthRedirect;
